import React from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isToday,
  isSameMonth,
  parseISO,
  isBefore,
  startOfDay,
  addDays,
  addMonths, // Added as per the example in the instruction
  subMonths, // Added as per the example in the instruction
  startOfWeek,
  endOfWeek,
  setHours,
  setMinutes,
  setSeconds,
  setMilliseconds,
  isAfter
} from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Facebook, Instagram, Linkedin, Building2, Twitter, Globe, AlertCircle, Film, Layers } from 'lucide-react';
import { getContentTypeBadgeColors } from './task/ReelsStoryConstants';
import { TaskHoverPreview } from './calendar/TaskHoverPreview';

interface CalendarProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  scheduledTasks: Array<{
    id: string;
    scheduled_for: string;
    platform: string;
    status: string;
    content: string;
    cycle: string;
    cycle_days?: string[];
    connected_accounts?: {
      name: string;
      platform: string;
    };
    posts?: Array<{
      id: string;
      status: string;
      metadata?: any;
      published_at?: string;
      table?: string;
      is_default?: boolean;
      platform_post_id?: string;
      platform_post_url?: string;
    }>;
  }>;
  onTaskSelect: (task: any) => void;
}

const renderPlatformIcon = (platform: string) => {
  const iconClass = "w-4 h-4 flex-shrink-0";
  switch (platform.toLowerCase()) {
    case 'facebook':
      return <Facebook className={`${iconClass} text-blue-600`} />;
    case 'instagram':
      return <Instagram className={`${iconClass} text-pink-600`} />;
    case 'linkedin':
      return <Linkedin className={`${iconClass} text-blue-700`} />;
    case 'google_business':
      return <Building2 className={`${iconClass} text-red-600`} />;
    case 'x':
      return <Twitter className={`${iconClass} text-black`} />;
    default:
      return <Globe className={`${iconClass} text-gray-600`} />;
  }
};

const dayNumberToId: { [key: number]: string } = {
  1: 'mon',
  2: 'tue',
  3: 'wed',
  4: 'thu',
  5: 'fri',
  6: 'sat',
  0: 'sun'
};

const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getPostStatusStyles = (task: any, day: Date) => {
  // Check for published posts first (HIGHEST priority)
  const publishedPost = task?.posts?.find(post =>
    (post.status === 'published' || post.platform_post_id || post.platform_post_url ||
     post.metadata?.platform_post_id || post.metadata?.platform_post_url) &&
    isSameDay(parseISO(post.published_at || post.created_at), day)
  );

  if (publishedPost) {
    return {
      background: 'bg-green-50 hover:bg-green-100',
      text: 'text-green-700',
      border: 'border-green-200',
      icon: 'fill-current'
    };
  }

  // Check for default ready posts (SECOND priority - before in_progress check!)
  // FIXED: Only show ready post for the day it was CREATED OR next day
  // For recurring tasks, scheduled_for changes but ready_post.created_at stays the same
  const defaultReadyPost = task?.posts?.find((post: any) =>
    post.table === 'ready_posts' &&
    post.is_default === true &&
    post.created_at &&
    (isSameDay(parseISO(post.created_at), day) || isSameDay(parseISO(post.created_at), addDays(day, 1)))
  );

  if (defaultReadyPost) {
    return {
      background: 'bg-purple-50 hover:bg-purple-100',
      text: 'text-purple-700',
      border: 'border-purple-200',
      icon: 'fill-current'
    };
  }

  // Check for posts in progress (THIRD priority - only for posts table, not ready_posts)
  const inProgressPost = task?.posts?.find(post =>
    post.table !== 'ready_posts' &&
    (post.metadata?.is_in_progress === true || post.status === 'pending')
  );

  if (inProgressPost) {
    const stage = inProgressPost.metadata?.stage;
    if (stage === 'publishing') {
      return {
        background: 'bg-indigo-50 hover:bg-indigo-100',
        text: 'text-indigo-700',
        border: 'border-indigo-200'
      };
    } else {
      return {
        background: 'bg-orange-50 hover:bg-orange-100',
        text: 'text-orange-700',
        border: 'border-orange-200'
      };
    }
  }
  
  // Handle ready_posts table entries that are not default — awaiting auto-promotion
  // FIXED: Show ready post for the day it was CREATED OR next day (in case cron ran after midnight)
  // Skip superseded posts (old posts replaced by newer defaults)
  const readyPost = task?.posts?.find((post: any) => {
    if ((post.table !== 'ready_posts' && post.status !== 'ready') || !post.created_at) return false;
    if (post.status === 'superseded') return false;
    
    const postDate = parseISO(post.created_at);
    return isSameDay(postDate, day) || isSameDay(postDate, addDays(day, 1));
  });

  if (readyPost) {
    return {
      background: 'bg-amber-50 hover:bg-amber-100',
      text: 'text-amber-600',
      border: 'border-amber-200'
    };
  }

  // Handle scheduled status for future recurring posts
  if (task?.status === 'scheduled') {
    return {
      background: 'bg-blue-50 hover:bg-blue-100',
      text: 'text-blue-700',
      border: 'border-blue-200'
    };
  }

  if (!task) {
    if (task?.status === 'active') {
      return {
        background: 'bg-blue-50 hover:bg-blue-100',
        text: 'text-blue-500',
        border: 'border-blue-200'
      };
    } else {
      return {
        background: 'bg-orange-50 hover:bg-orange-100',
        text: 'text-orange-500',
        border: 'border-orange-200'
      };
    }
  }

  // First check task status
  if (task.status !== 'active') {
    return {
      background: 'bg-gray-50 hover:bg-gray-100',
      text: 'text-gray-500',
      bgColor: 'bg-gray-50'
    };
  }

  // Get ALL posts for this task (check status regardless of date match)
  const allTaskPosts = task.posts || [];

  // Check for ready_posts first (before in_progress check!)
  const defaultReadyPostAny = allTaskPosts.find((post: any) =>
    post.table === 'ready_posts' && post.is_default === true
  );

  if (defaultReadyPostAny) {
    return {
      background: 'bg-purple-50 hover:bg-purple-100',
      text: 'text-purple-700',
      border: 'border-purple-200',
      icon: 'fill-current'
    };
  }

  // Check for any posts in progress (only for posts table, not ready_posts)
  const inProgressPosts = allTaskPosts.filter((post: any) =>
    post.table !== 'ready_posts' &&
    (post.metadata?.is_in_progress === true || post.status === 'pending')
  );

  if (inProgressPosts.length > 0) {
    const latestInProgress = inProgressPosts[inProgressPosts.length - 1];
    const stage = latestInProgress.metadata?.stage;

    if (stage === 'publishing') {
      return {
        background: 'bg-indigo-50 hover:bg-indigo-100',
        text: 'text-indigo-700',
        border: 'border-indigo-200'
      };
    } else {
      return {
        background: 'bg-orange-50 hover:bg-orange-100',
        text: 'text-orange-700',
        border: 'border-orange-200'
      };
    }
  }

  // Get posts for this specific day
  const postsForDay = allTaskPosts.filter((post: any) =>
    isSameDay(parseISO(post.published_at || post.created_at), day)
  );

  if (!postsForDay.length) {
    return {
      background: 'bg-blue-50 hover:bg-blue-100',
      text: 'text-blue-700',
      border: 'border-blue-200'
    };
  }

  // Get the most relevant post status
  const postStatuses = postsForDay.map(post => ({
    status: post.status,
    isInProgress: post.metadata?.is_in_progress === true,
    stage: post.metadata?.stage,
    table: post.table,
    is_default: post.is_default,
    platform_post_id: post.platform_post_id,
    platform_post_url: post.platform_post_url,
    metadata: post.metadata
  }));

  // Check for published posts first
  if (postStatuses.some(p => 
    p.status === 'published' || 
    p.platform_post_id || 
    p.platform_post_url || 
    p.metadata?.platform_post_id || 
    p.metadata?.platform_post_url
  )) {
    return {
      background: 'bg-green-50 hover:bg-green-100',
      text: 'text-green-700',
      border: 'border-green-200',
      icon: 'fill-current'
    };
  }

  // Check for default ready posts
  if (postStatuses.some(p => p.table === 'ready_posts' && p.is_default === true)) {
    return {
      background: 'bg-purple-50 hover:bg-purple-100',
      text: 'text-purple-700',
      border: 'border-purple-200',
      icon: 'fill-current'
    };
  }

  // Check for error posts
  if (postStatuses.some(p => p.status === 'error')) {
    return {
      background: 'bg-red-50 hover:bg-red-100',
      text: 'text-red-700',
      border: 'border-red-200'
    };
  }

  // Check for ready posts (non-default) — awaiting auto-promotion
  if (postStatuses.some(p => p.status === 'ready' || p.table === 'ready_posts')) {
    return {
      background: 'bg-amber-50 hover:bg-amber-100',
      text: 'text-amber-600',
      border: 'border-amber-200'
    };
  }

  // Handle posts in progress with stages
  if (postStatuses.some(p => p.isInProgress)) {
    const inProgressPost = postStatuses.find(p => p.isInProgress);
    switch (inProgressPost?.stage) {
      case 'publishing':
        return {
          background: 'bg-indigo-50 hover:bg-indigo-100',
          text: 'text-indigo-700',
          border: 'border-indigo-200'
        };
      case 'creation':
      case 'webhook_processing':
      default:
        return {
          background: 'bg-orange-50 hover:bg-orange-100',
          text: 'text-orange-700',
          border: 'border-orange-200'
        };
    }
  }

  // Default pending state
  return {
    background: 'bg-gray-50 hover:bg-gray-100',
    text: 'text-gray-500',
    border: 'border-gray-200'
  };
};

// Helper function to get the next occurrence of a task for a given day
const getNextOccurrence = (task: any, day: Date): Date | null => {
  const scheduledTime = parseISO(task.scheduled_for);
  const result = new Date(day);
  result.setHours(scheduledTime.getHours());
  result.setMinutes(scheduledTime.getMinutes());
  result.setSeconds(0);
  result.setMilliseconds(0);

  // For non-cyclic tasks, only return if it's the exact scheduled date
  if (task.cycle === 'once') {
    return isSameDay(day, scheduledTime) ? result : null;
  }

  // FIX: For cyclic tasks, scheduled_for advances to the NEXT occurrence after each publish.
  // This means scheduled_for could be weeks/months in the future.
  // We can't use scheduled_for as a "start date" guard — instead use task.created_at
  // (the original creation date) to determine when the cycle started.
  const cycleStartDate = task.created_at ? parseISO(task.created_at) : scheduledTime;
  if (isBefore(day, startOfDay(cycleStartDate))) {
    return null;
  }

  // Then check if this day matches the cycle pattern
  switch (task.cycle) {
    case 'daily':
      return result;

    case 'weekly':
      return day.getDay() === scheduledTime.getDay() ? result : null;

    case 'monthly':
      return day.getDate() === scheduledTime.getDate() ? result : null;

    case 'custom':
      return task.cycle_days?.includes(dayNumberToId[day.getDay()]) ? result : null;

    default:
      return null;
  }
};

// Helper function to check if a task should be displayed on a given day
const shouldDisplayTask = (task: any, day: Date): boolean => {
  // CRITICAL FIX: Check for posts on this day FIRST
  // Posts should be visible even if scheduled_for was moved to next cycle
  const hasPostForDay = task.posts?.some(post =>
    isSameDay(parseISO(post.published_at || post.created_at), day)
  );

  // If there's a post for this day, always show it
  if (hasPostForDay) {
    return true;
  }

  // Otherwise, check if this day matches the task's schedule
  const nextOccurrence = getNextOccurrence(task, day);
  if (!nextOccurrence) {
    return false;
  }

  // FIX: For active cyclic tasks, show the scheduled slot even if scheduled_for
  // advanced past this day (getNextOccurrence already validates the day/pattern match).
  // Only hide inactive tasks or days before the task was originally created.
  return task.status === 'active';
};

// Helper function to get task with adjusted scheduled time for display
const getAdjustedTask = (task: any, day: Date): any => {
  // Get posts for this specific day
  const postsForDay = task.posts?.filter(post =>
    isSameDay(parseISO(post.published_at || post.created_at), day)
  ) || [];

  // CRITICAL FIX: If there are posts for this day, use their time
  // This handles recurring tasks where scheduled_for moved to next cycle
  if (postsForDay.length > 0) {
    // Find the most recent post to use as display time
    const latestPost = postsForDay.reduce((latest, current) => {
      const latestTime = parseISO(latest.published_at || latest.created_at);
      const currentTime = parseISO(current.published_at || current.created_at);
      return currentTime > latestTime ? current : latest;
    });

    const displayTime = parseISO(latestPost.published_at || latestPost.created_at);

    return {
      ...task,
      scheduled_for: displayTime.toISOString(),
      display_date: displayTime,
      posts: postsForDay
    };
  }

  // Otherwise, use the scheduled occurrence
  const nextOccurrence = getNextOccurrence(task, day);
  if (!nextOccurrence) {
    return null;
  }

  return {
    ...task,
    scheduled_for: nextOccurrence.toISOString(),
    display_date: nextOccurrence,
    posts: postsForDay
  };
};

export const Calendar: React.FC<CalendarProps> = ({
  selectedDate,
  onDateSelect,
  scheduledTasks,
  onTaskSelect,
}) => {
  const monthStart = startOfMonth(selectedDate);
  const monthEnd = endOfMonth(selectedDate);
  
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const handlePrevMonth = () => {
    onDateSelect(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    onDateSelect(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1));
  };

  const getTasksForDay = (day: Date) => {
    return scheduledTasks
      .filter(task => shouldDisplayTask(task, day))
      .map(task => getAdjustedTask(task, day))
      .filter(Boolean)
      .sort((a, b) => {
        const timeA = a.display_date || parseISO(a.scheduled_for);
        const timeB = b.display_date || parseISO(b.scheduled_for);
        return timeA.getTime() - timeB.getTime();
      });
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center space-x-2">
            <CalendarIcon className="w-5 h-5 text-gray-500" />
            <span className="hidden sm:inline">{format(selectedDate, 'MMMM yyyy')}</span>
            <span className="sm:hidden">{format(selectedDate, 'MMM yyyy')}</span>
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-2 sm:p-4">
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {weekDays.map((day) => (
            <div
              key={day}
              className="text-center text-sm font-medium text-gray-500 py-2"
            >
              <span className="hidden sm:inline">{day}</span>
              <span className="sm:hidden">{day.charAt(0)}</span>
            </div>
          ))}
          {days.map((day, dayIndex) => {
            const tasksForDay = getTasksForDay(day);
            const columnIndex = dayIndex % 7;

            return (
              <div
                key={day.toISOString()}
                className={`
                  min-h-[60px] sm:min-h-[100px] p-1 sm:p-2 border rounded-lg
                  ${!isSameMonth(day, selectedDate) ? 'bg-gray-50' : 'hover:bg-blue-50'}
                  ${isSameDay(day, selectedDate) ? 'bg-blue-50 border-blue-200' : ''}
                  ${isToday(day) ? 'border-blue-500' : 'border-gray-200'}
                `}
                onClick={() => onDateSelect(day)}
              >
                <div className="text-sm font-medium mb-1">
                  {format(day, 'd')}
                </div>
                <div className="space-y-1.5 overflow-y-auto max-h-[80px] sm:max-h-[120px]">
                  {tasksForDay.map((task) => {
                    const styles = getPostStatusStyles(task, day);
                    const displayTime = task.display_date || parseISO(task.scheduled_for);

                    const hasPublishedPost = task.posts?.some(post => {
                      const isPublished = post.status === 'published' ||
                        post.platform_post_id ||
                        post.platform_post_url ||
                        post.metadata?.platform_post_id ||
                        post.metadata?.platform_post_url;

                      if (!isPublished) return false;

                      if (task.cycle && task.cycle !== 'once') {
                        const taskDate = new Date(task.scheduled_for);
                        taskDate.setHours(0, 0, 0, 0);
                        const postDate = new Date(post.created_at);
                        postDate.setHours(0, 0, 0, 0);
                        return postDate.getTime() === taskDate.getTime();
                      }

                      return true;
                    });

                    const hasDefaultReadyPost = task.posts?.some(post =>
                      post.table === 'ready_posts' && post.is_default === true
                    );

                    const hasAwaitingReadyPost = task.posts?.some(post =>
                      post.table === 'ready_posts' && !post.is_default
                    );

                    // Only show error if no published/ready post exists and the LATEST post has error status
                    // If a newer non-error post exists (including ready_posts), the error is from a previous execution
                    // Skip silently expired posts - they were auto-cleaned from the queue
                    const errorPost = task.posts?.find(post =>
                      post.status === 'error' &&
                      !post.metadata?.silently_expired &&
                      isSameDay(parseISO(post.created_at), day)
                    );
                    const latestPostForDay = task.posts
                      ?.filter(post => isSameDay(parseISO(post.created_at), day) && !post.metadata?.silently_expired)
                      ?.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())?.[0];
                    const hasError = !!errorPost && !hasPublishedPost && !hasDefaultReadyPost && (!latestPostForDay || latestPostForDay.status === 'error');
                    const errorMessage = errorPost?.metadata?.error || 'Publication failed';

                    // Determine inline status label
                    const getInlineStatus = () => {
                      if (hasPublishedPost) return { text: '✓', className: 'text-green-600' };
                      if (hasDefaultReadyPost) return { text: 'Default Ready', className: 'text-purple-600' };
                      if (hasError) return { text: 'Error', className: 'text-red-600' };
                      if (hasAwaitingReadyPost) return { text: 'Ready', className: 'text-amber-600' };
                      return null;
                    };
                    const inlineStatus = getInlineStatus();

                    return (
                      <TaskHoverPreview
                        key={`${task.id}-${format(displayTime, 'HH:mm')}`}
                        task={task}
                        day={day}
                        columnIndex={columnIndex}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onTaskSelect({
                              ...task,
                              scheduled_for: displayTime.toISOString()
                            });
                          }}
                          className={`
                            w-full text-left text-xs p-2 rounded border relative group
                            ${hasError ? 'bg-red-50 hover:bg-red-100 text-red-700 border-red-300' : `${styles.background} ${styles.text} ${styles.border}`}
                            transition-colors duration-150
                          `}
                          title={hasError ? errorMessage : undefined}
                        >
                          <div className="flex items-center gap-2">
                            {hasError ? (
                              <AlertCircle className="w-4 h-4 flex-shrink-0 text-red-500" />
                            ) : (
                              <span className="flex items-center gap-0.5">
                                {renderPlatformIcon(task.platform)}
                                {task.content_type === 'reel' && <Film className="w-3 h-3 text-purple-500" />}
                                {task.content_type === 'story' && <Layers className="w-3 h-3 text-pink-500" />}
                              </span>
                            )}
                            <span className="truncate flex-1">
                              <span className="font-medium">{format(displayTime, 'HH:mm')}</span>
                              {task.content_type && task.content_type !== 'post' && (
                                <span className={`hidden sm:inline ml-1 text-[10px] font-bold px-1 py-0.5 rounded ${getContentTypeBadgeColors(task.content_type).bg} ${getContentTypeBadgeColors(task.content_type).text}`}>
                                  {task.content_type === 'reel' ? 'Reel' : 'Story'}
                                </span>
                              )}
                              {inlineStatus && (
                                <span className={`hidden sm:inline ml-1 font-semibold ${inlineStatus.className}`}>{inlineStatus.text}</span>
                              )}
                              <span className="hidden sm:inline opacity-75"> - {hasError ? 'Error' : task.content}</span>
                            </span>
                            {hasError && (
                              <span className="hidden group-hover:block absolute left-0 right-0 -bottom-1 translate-y-full z-10 bg-red-600 text-white text-xs p-2 rounded shadow-lg max-w-xs whitespace-normal">
                                {errorMessage}
                              </span>
                            )}
                          </div>
                        </button>
                      </TaskHoverPreview>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};